import React, { useState } from "react";
import { QrScanner } from "../components/QrScanButton";
import api, { fmtErr } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScanLine, CheckCircle2, XCircle, Camera, CircleCheck, RotateCcw, AlertTriangle, UserCheck, Clock, Users, Ticket, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const istDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

// Result banner: green = let in, orange = already used (who / when), red = not valid.
function Banner({ tone, Icon, title, children, testid }) {
  const cls = { green: "from-[#0F6B3A] to-[#1F9D55]", orange: "from-[#B4530F] to-[#E2741C]", red: "from-[#8E0F1F] to-[#D2322D]" }[tone];
  return (
    <div className={`rounded-2xl bg-gradient-to-r ${cls} text-white px-4 py-4 shadow-lg flex items-center gap-3`} data-testid={testid}>
      <span className="w-12 h-12 rounded-full bg-white/20 border border-white/40 flex items-center justify-center shrink-0"><Icon className="w-7 h-7" /></span>
      <div className="min-w-0">
        <p className="font-heading text-xl sm:text-2xl font-bold tracking-wider leading-tight">{title}</p>
        <div className="text-[13px] sm:text-sm text-white/90 leading-snug mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, mono, wide }) {
  return (
    <div className={`rounded-xl bg-[#FBF7EF] border border-[#EFE3CC] px-3 py-2 min-w-0 ${wide ? "col-span-2" : ""}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gold-700 flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />}{label}</p>
      <p className={`text-sm font-semibold text-slate-800 ${mono ? "font-mono break-all" : "break-words"}`}>{value || "-"}</p>
    </div>
  );
}

export default function Verification() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const scan = async (val) => {
    const c = typeof val === "string" ? val : code;
    if (!c.trim()) return;
    setLoading(true);
    setDone(false);
    try {
      const r = await api.post("/verification/scan", { code: c.trim() });
      setResult(r.data);
      if (navigator.vibrate) navigator.vibrate(r.data.valid ? 80 : [80, 60, 80]);
    } catch (e) {
      toast.error(fmtErr(e));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const complete = async () => {
    setLoading(true);
    try {
      await api.post("/verification/complete", { transaction_id: result.transaction.transaction_id });
      setDone(true);
      toast.success("Darshan verification completed");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  const next = () => { setResult(null); setDone(false); setCode(""); setScanning(true); };
  const t = result?.transaction;

  return (
    <div className="max-w-3xl mx-auto pb-28 sm:pb-0" data-testid="verification-page">
      <div className="mb-4">
        <h1 className="font-heading text-xl sm:text-3xl font-bold text-maroon-900 leading-tight">Entrance Verification</h1>
        <p className="text-[13px] sm:text-sm text-slate-500">Scan the ticket QR code, or enter the Transaction ID / ticket code.</p>
      </div>

      {!result && (
        <div className="bg-slate-900 rounded-2xl overflow-hidden shadow-lg" data-testid="scanner-panel">
          {scanning ? (
            <div>
              <QrScanner className="min-h-[55vh] sm:min-h-[380px]" onScan={(txt) => { setScanning(false); setCode(txt); scan(txt); }} onError={() => setScanning(false)} />
              <div className="p-3 flex justify-center">
                <Button variant="outline" onClick={() => setScanning(false)} className="bg-white/10 text-white border-white/30 hover:bg-white/20">Stop Scanner</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16 gap-7">
              <div className="w-40 h-40 sm:w-52 sm:h-52 rounded-3xl border-4 border-dashed border-slate-500 flex items-center justify-center">
                <ScanLine className="w-20 h-20 text-slate-400" />
              </div>
              <Button onClick={() => setScanning(true)} disabled={loading} data-testid="scanner-start-btn"
                className="h-14 px-10 text-lg bg-white text-slate-900 hover:bg-gold-50 rounded-xl font-semibold">
                <Camera className="w-6 h-6 mr-2" /> Start Scanner
              </Button>
            </div>
          )}
          <div className="bg-slate-800 px-4 py-4">
            <p className="text-center text-sm text-slate-300 mb-2">Or enter the code manually</p>
            <div className="flex gap-2">
              <Input data-testid="scanner-manual-code-input" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. SPD-PROT20260925000001" className="h-12 text-base font-mono bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                onKeyDown={(e) => e.key === "Enter" && scan()} />
              <Button data-testid="scanner-verify-btn" onClick={scan} disabled={loading} title="Verify"
                className="h-12 w-14 shrink-0 bg-maroon-700 hover:bg-maroon-800 text-white">
                <CircleCheck className="w-6 h-6" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-3" data-testid="scanner-status-result">
          {done ? (
            <Banner tone="green" Icon={CheckCircle2} title="DARSHAN COMPLETED" testid="scanner-completed-banner">Entry recorded. Scan the next ticket.</Banner>
          ) : result.valid ? (
            <Banner tone="green" Icon={CheckCircle2} title="VALID TICKET" testid="scanner-valid-banner">Check the photo and ID, then confirm entry.</Banner>
          ) : result.already ? (
            <Banner tone="orange" Icon={AlertTriangle} title="ALREADY VERIFIED" testid="scanner-already-banner">
              <span data-testid="scanner-invalid-reason">This ticket was used once already — do not allow entry again.</span>
              <span className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 font-semibold text-white">
                <span className="inline-flex items-center gap-1" data-testid="scanner-verified-by"><UserCheck className="w-4 h-4" />{result.verified_by || "-"}</span>
                <span className="inline-flex items-center gap-1" data-testid="scanner-verified-at"><Clock className="w-4 h-4" />{istDateTime(result.verified_at)}</span>
              </span>
            </Banner>
          ) : (
            <Banner tone="red" Icon={XCircle} title="INVALID" testid="scanner-invalid-banner"><span data-testid="scanner-invalid-reason">{result.reason}</span></Banner>
          )}

          {t && (
            <div className="rounded-2xl bg-white border border-[#E5D9C5] shadow-sm overflow-hidden" data-testid="scanner-ticket-card">
              <div className="flex gap-3 p-3 sm:p-4 bg-gradient-to-r from-[#FFF8EC] to-white border-b border-[#EFE3CC]">
                {t.photo ? (
                  <img src={t.photo} alt="devotee" className="w-24 h-28 sm:w-28 sm:h-32 object-cover rounded-xl border-2 border-gold-400 shrink-0" data-testid="scanner-devotee-photo" />
                ) : (
                  <div className="w-24 h-28 sm:w-28 sm:h-32 rounded-xl border-2 border-dashed border-gold-400 flex items-center justify-center text-xs text-slate-400 shrink-0">No photo</div>
                )}
                <div className="min-w-0 flex-1 flex flex-col justify-center gap-1">
                  <p className="font-heading text-lg sm:text-xl font-bold text-maroon-900 leading-tight break-words">{t.devotee_name}</p>
                  <p className="font-mono text-sm font-bold text-maroon-700 break-all">{t.ticket?.ticket_no || "-"}</p>
                  <p className="text-sm text-slate-600">{t.darshan_type_name}</p>
                  <div><StatusBadge status={t.status} /></div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 sm:p-4">
                <Info icon={Users} label="Devotees" value={String(t.num_devotees)} />
                <Info icon={CalendarDays} label="Darshan Date" value={t.darshan_date ? t.darshan_date.split("-").reverse().join("/") : "-"} />
                <Info icon={Clock} label="Issued" value={istDateTime(t.ticket?.issued_at)} />
                {t.verified_at && <Info icon={UserCheck} label="Verified By" value={t.verified_by} />}
                {t.verified_at && <Info icon={Clock} label="Verified At" value={istDateTime(t.verified_at)} />}
                <Info icon={Ticket} label="Transaction ID" value={t.transaction_id} mono wide />
                {t.aadhaar && <Info label="Aadhaar" value={t.aadhaar} mono />}
              </div>
            </div>
          )}

          {/* Actions: pinned to the bottom on phones so the verifier never scrolls to confirm. */}
          <div className="fixed sm:static bottom-0 inset-x-0 z-20 bg-white/95 sm:bg-transparent backdrop-blur sm:backdrop-blur-none border-t sm:border-0 border-[#E5D9C5] p-3 sm:p-0 space-y-2 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] sm:shadow-none">
            {result.valid && !done && (
              <Button onClick={complete} disabled={loading} data-testid="scanner-complete-btn"
                className="w-full h-14 bg-green-700 hover:bg-green-800 text-white font-heading tracking-wider text-base rounded-xl">
                <CheckCircle2 className="w-5 h-5 mr-2" /> CONFIRM ENTRY
              </Button>
            )}
            <Button onClick={next} variant={result.valid && !done ? "outline" : "default"} data-testid="scanner-next-btn"
              className={`w-full h-12 rounded-xl ${result.valid && !done ? "" : "bg-maroon-700 hover:bg-maroon-800 text-white"}`}>
              <RotateCcw className="w-5 h-5 mr-2" /> Scan Next Ticket
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
